import { EditTwoTone } from '@ant-design/icons';
import { Button } from 'antd';
import React, { type FC, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';

import {
  EAbilityActions,
  type EAbilitySubjects,
} from '@client/hooks/useAbilities';
import { useAuth } from '@client/hooks/useAuth';
import { useLoading } from '@client/store/loading.store';
import { COLORS } from '@client/utils/colors.util';

import type { ButtonType } from 'antd/es/button';

type TEditAction = {
  subject: string | keyof typeof EAbilitySubjects;
  entityId: string;
  type?: ButtonType;
  showLabel?: boolean;
  isLoading?: boolean;
  disabled?: boolean;
  onEdit?: (id: string) => void;
};

/**
 * A component that renders an edit button for the given `entityId`.
 * The button is disabled if the user doesn't have enough permissions to update the given `entityId`.
 * The button is also disabled if the component is in a loading state.
 * When the button is clicked, the `onEdit` function will be called with the given `entityId`.
 * @param {TEditAction} props The props for the component
 * @param {string} props.entityId The id of the entity to be updated
 * @param {ButtonType} props.type The type of button
 * @param {boolean} props.showLabel Whether or not the component should show a label
 * @param {boolean} props.isLoading Whether or not the component should be in a loading state
 * @param {boolean} props.disabled Whether or not the component should be disabled
 * @param {(id: string) => void} props.onEdit The callback function to be called when the button is clicked
 * @returns {JSX.Element|null} The JSX element of the component
 */
export const EditAction: FC<TEditAction> = (props): JSX.Element | null => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const { ability } = useAuth();
  const { loading } = useLoading();

  const {
    subject,
    entityId,
    type = 'primary',
    showLabel = false,
    onEdit,
  } = props;

  /**
   * Prevents the default link behavior and stops the event propagation.
   * This is useful when we want to handle the click event manually.
   * @param {React.MouseEvent<HTMLAnchorElement, MouseEvent>} e The event object
   * @returns {void}
   */
  const handleClick = (
    e: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
  ): void => {
    e.preventDefault();
  };

  return (
    <a
      title={t('actions.edit', { type: '' }) as string}
      onClick={handleClick}
      href={`${pathname}/${entityId}`}
      rel="noopener noreferrer"
    >
      <Button
        disabled={ability.cannot(EAbilityActions.UPDATE, subject)}
        loading={loading}
        type={type}
        icon={<EditTwoTone twoToneColor={COLORS.success} />}
        onClick={() => onEdit?.(entityId)}
      >
        {showLabel && t('actions.edit', { type: '' })}
      </Button>
    </a>
  );
};
